import React, { useEffect } from 'react';
import StNavbar from '../../components/StNavbar';
import StFooter from '../../components/StFooter';
import { SetSeo } from '../../lib/common_funcs';

import '../../redesign-2025.css';
import AOS from 'aos';
import 'aos/dist/aos.css';

function FeaturePageTemplate({ feature }) {
    useEffect(() => {
        AOS.init({
            duration: 800,
            once: true,
            easing: 'ease-out-cubic',
            offset: 50
        });
    }, []);

    const faqs = feature.faqs || [];

    const featureSeo = {
        title: `${feature.name} | TurboSteed Features`,
        description: feature.description,
        canonicalUrl: `https://turbosteed.com/features/${feature.slug}`,
        keywords: `${feature.name}, website features, TurboSteed`,
        ogTitle: feature.tagline,
        ogImage: feature.heroImage
            ? `https://turbosteed.com${feature.heroImage}`
            : "https://turbosteed.com/turbo-navbar-logo.png",
    };

    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": feature.name,
        "description": feature.description
    };

    const allParams = {
        "seo": featureSeo,
        "schema": schemaMarkup
    };

    return (
        <div className="ts-redesign">
            <SetSeo {...allParams} />
            <StNavbar />

            {/* Hero Section */}
            <header className="ts-hero" style={{minHeight: 'auto', paddingBottom: '4rem'}}>
                <div className="ts-hero-bg">
                    <div className="ts-gradient-orb ts-orb-1"></div>
                    <div className="ts-gradient-orb ts-orb-2"></div>
                    <div className="ts-gradient-orb ts-orb-3"></div>
                </div>
                <div className="ts-grid-pattern"></div>

                <div className="container ts-hero-content">
                    {/* Side-by-side layout */}
                    {feature.heroLayout === 'side-by-side' && feature.heroImage ? (
                        <div className="row align-items-center">
                            <div className="col-lg-6 text-center text-lg-start" data-aos="fade-up">
                                <div className="ts-bento-card-icon" style={{margin: '0 0 1.5rem', width: '80px', height: '80px'}}>
                                    <i className={feature.icon} style={{fontSize: '2rem'}}></i>
                                </div>
                                <h1 className="ts-hero-title">
                                    <span className="ts-gradient-text">{feature.tagline}</span>
                                </h1>
                                <p className="ts-hero-subtitle" style={{maxWidth: '600px', margin: '1rem 0 2rem'}}>
                                    {feature.description}
                                </p>
                                {feature.heroNote && (
                                    <p className="ts-hero-note" style={{maxWidth: '600px', margin: '0 0 2rem', fontSize: '0.95rem', color: 'var(--ts-gray)', fontStyle: 'italic'}}>
                                        {feature.heroNote}
                                    </p>
                                )}
                                <div className="ts-hero-cta justify-content-center justify-content-lg-start">
                                    <a href="https://dashboard.turbosteed.com" className="ts-btn ts-btn-primary">
                                        Try It Free
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14M12 5l7 7-7 7"/>
                                        </svg>
                                    </a>
                                    {feature.secondaryCta && (
                                        <a href={feature.secondaryCta.url} className="ts-btn ts-btn-glass">
                                            {feature.secondaryCta.text}
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="col-lg-6 mt-5 mt-lg-0" data-aos="fade-up" data-aos-delay="200">
                                <img
                                    src={feature.heroImage}
                                    alt={feature.name}
                                    className="img-fluid rounded shadow-lg"
                                    style={{
                                        maxWidth: feature.heroImageMaxWidth || '100%',
                                        border: '1px solid rgba(255,255,255,0.1)'
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        /* Default centered layout */
                        <div className="text-center" data-aos="fade-up">
                            <div className="ts-bento-card-icon" style={{margin: '0 auto 1.5rem', width: '80px', height: '80px'}}>
                                <i className={feature.icon} style={{fontSize: '2rem'}}></i>
                            </div>
                            <h1 className="ts-hero-title">
                                <span className="ts-gradient-text">{feature.tagline}</span>
                            </h1>
                            <p className="ts-hero-subtitle" style={{maxWidth: '600px', margin: '1rem auto 2rem'}}>
                                {feature.description}
                            </p>
                            {feature.heroNote && (
                                <p className="ts-hero-note" style={{maxWidth: '600px', margin: '0 auto 2rem', fontSize: '0.95rem', color: 'var(--ts-gray)', fontStyle: 'italic'}}>
                                    {feature.heroNote}
                                </p>
                            )}
                            <div className="ts-hero-cta justify-content-center">
                                <a href="https://dashboard.turbosteed.com" className="ts-btn ts-btn-primary">
                                    Try It Free
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </a>
                                {feature.secondaryCta && (
                                    <a href={feature.secondaryCta.url} className="ts-btn ts-btn-glass">
                                        {feature.secondaryCta.text}
                                    </a>
                                )}
                            </div>

                            {/* Hero Images - side by side if multiple */}
                            {feature.heroImages && feature.heroImages.length > 0 && (
                                <div className="mt-5 ts-hero-images" data-aos="fade-up" data-aos-delay="200">
                                    {feature.heroImages.map((img, index) => (
                                        <img
                                            key={index}
                                            src={img.src}
                                            alt={img.alt || feature.name}
                                            className="img-fluid rounded shadow-lg"
                                            style={{
                                                border: '1px solid rgba(255,255,255,0.1)'
                                            }}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Hero Image - single centered */}
                            {feature.heroImage && !feature.heroImages && (
                                <div className="mt-5" data-aos="fade-up" data-aos-delay="200">
                                    <img
                                        src={feature.heroImage}
                                        alt={feature.name}
                                        className="img-fluid rounded shadow-lg"
                                        style={{
                                            maxWidth: feature.heroImageMaxWidth || '100%',
                                            border: '1px solid rgba(255,255,255,0.1)'
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <main className="main-content">
                {/* How It Works */}
                {feature.howItWorks && (
                    <section className="ts-section ts-section-light">
                        <div className="container">
                            <header className="ts-section-header" data-aos="fade-up">
                                <h2 className="ts-section-title">How It <span className="ts-text-gradient">Works</span></h2>
                            </header>

                            <div className="row g-4 justify-content-center">
                                {feature.howItWorks.map((step, index) => (
                                    <div className="col-md-4" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                                        <div className="ts-bento-card h-100 text-center">
                                            <div className="ts-step-number">{index + 1}</div>
                                            <h3>{step.title}</h3>
                                            <p>{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Key Capabilities */}
                <section className="ts-section ts-section-gradient-1">
                    <div className="container">
                        <header className="ts-section-header" data-aos="fade-up">
                            <h2 className="ts-section-title">Key <span className="ts-text-gradient">Capabilities</span></h2>
                        </header>

                        <div className="row g-4 justify-content-center">
                            {feature.capabilities.map((capability, index) => (
                                <div className="col-md-6 col-lg-3" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                                    <div className="ts-bento-card h-100 text-center">
                                        <i className="fa fa-check-circle fa-2x text-success mb-3"></i>
                                        <p className="mb-0"><strong>{capability}</strong></p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Use Cases */}
                {feature.useCases && (
                    <section className="ts-section ts-section-light">
                        <div className="container">
                            <header className="ts-section-header" data-aos="fade-up">
                                <h2 className="ts-section-title">Who It's <span className="ts-text-gradient">For</span></h2>
                            </header>

                            <div className="row g-4 justify-content-center">
                                {feature.useCases.map((useCase, index) => (
                                    <div className="col-md-4" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                                        <div className="ts-bento-card h-100">
                                            <h3>{useCase.title}</h3>
                                            <p>{useCase.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* FAQ */}
                {faqs.length > 0 && (
                    <section className="ts-section ts-section-gradient-2">
                        <div className="container">
                            <header className="ts-section-header" data-aos="fade-up">
                                <h2 className="ts-section-title">Frequently Asked <span className="ts-text-gradient">Questions</span></h2>
                            </header>

                            <div className="row justify-content-center">
                                <div className="col-lg-8">
                                    <div className="ts-faq-list" data-aos="fade-up" data-aos-delay="100">
                                        {faqs.map((faq, index) => (
                                            <div className="ts-faq-item" key={index}>
                                                <h4>{faq.question}</h4>
                                                <p>{faq.answer}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* CTA */}
                <section className="ts-section ts-section-dark">
                    <div className="container text-center">
                        <h2 className="text-white mb-4" data-aos="fade-up">Ready to Get Started?</h2>
                        <p className="lead text-white-50 mb-5" data-aos="fade-up" data-aos-delay="100">Create your website for free. No credit card required.</p>
                        <div data-aos="fade-up" data-aos-delay="200">
                            <a href="https://dashboard.turbosteed.com" className="ts-btn ts-btn-light ts-btn-lg">
                                Start Free
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            <StFooter />
        </div>
    );
}

export default FeaturePageTemplate;
